import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkle, Plus, Trash, FloppyDisk, ArrowClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyOption = () => ({ label: '', keywords: [], category_slug: '', price_min: '', price_max: '' });
const emptyQuestion = () => ({ id: `q-${Date.now()}`, question: '', options: [emptyOption()] });

const KioskQuestionsAdminPage = () => {
  const [questions, setQuestions] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/admin/kiosk/questions`, { withCredentials: true }).then(r => setQuestions(r.data.questions || [])).catch(() => {}),
      axios.get(`${API_URL}/api/s/mrg/categories`).then(r => setCats(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const update = (qi, patch) => setQuestions(qs => qs.map((q, i) => i === qi ? { ...q, ...patch } : q));
  const updateOpt = (qi, oi, patch) => setQuestions(qs => qs.map((q, i) => i === qi ? { ...q, options: q.options.map((o, j) => j === oi ? { ...o, ...patch } : o) } : q));
  const addQuestion = () => setQuestions(qs => [...qs, emptyQuestion()]);
  const removeQuestion = (qi) => setQuestions(qs => qs.filter((_, i) => i !== qi));
  const addOption = (qi) => setQuestions(qs => qs.map((q, i) => i === qi ? { ...q, options: [...q.options, emptyOption()] } : q));
  const removeOption = (qi, oi) => setQuestions(qs => qs.map((q, i) => i === qi ? { ...q, options: q.options.filter((_, j) => j !== oi) } : q));

  const save = async () => {
    setSaving(true); setSaved(false);
    const payload = questions.map(q => ({
      id: q.id, question: q.question,
      options: (q.options || []).map(o => {
        const opt = { label: o.label };
        const kw = Array.isArray(o.keywords) ? o.keywords : String(o.keywords || '').split(',').map(s => s.trim()).filter(Boolean);
        if (kw.length) opt.keywords = kw;
        if (o.category_slug) opt.category_slug = o.category_slug;
        if (o.price_min !== '' && o.price_min != null) opt.price_min = parseFloat(o.price_min);
        if (o.price_max !== '' && o.price_max != null) opt.price_max = parseFloat(o.price_max);
        return opt;
      }),
    }));
    try {
      await axios.put(`${API_URL}/api/admin/kiosk/questions`, { questions: payload }, { withCredentials: true });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) { /* noop */ } finally { setSaving(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-[900px] mx-auto" data-testid="admin-assistant">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-[24px] font-bold text-slate-900 flex items-center gap-2"><Sparkle size={24} weight="fill" className="text-rose-500" /> Assistant borne — Questions</h1>
          <p className="text-slate-500 text-[14px] mt-1">Configurez les questions de découverte. Chaque réponse filtre les montures proposées (catégorie, mots-clés, budget).</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[13px] font-semibold flex items-center gap-2"><ArrowClockwise size={15} weight="bold" /> Recharger</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-[13px] font-semibold flex items-center gap-2 disabled:opacity-50" data-testid="save-questions"><FloppyDisk size={15} weight="bold" /> {saving ? 'Enregistrement…' : saved ? 'Enregistré ✓' : 'Enregistrer'}</button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-5">
          {questions.map((q, qi) => (
            <div key={qi} className="bg-white rounded-2xl border border-slate-100 p-4 md:p-5" data-testid={`question-${qi}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[12px] font-bold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-lg shrink-0">Q{qi + 1}</span>
                <input value={q.question} onChange={e => update(qi, { question: e.target.value })} placeholder="Texte de la question…"
                  className="flex-1 h-11 px-3 rounded-lg border border-slate-200 outline-none focus:border-slate-400 text-[15px] font-semibold" />
                <button onClick={() => removeQuestion(qi)} className="text-slate-300 hover:text-rose-500 shrink-0" title="Supprimer la question"><Trash size={18} weight="bold" /></button>
              </div>
              <div className="space-y-2">
                {(q.options || []).map((o, oi) => (
                  <div key={oi} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-slate-50 rounded-xl p-2.5">
                    <input value={o.label} onChange={e => updateOpt(qi, oi, { label: e.target.value })} placeholder="Réponse"
                      className="md:col-span-3 h-9 px-2.5 rounded-lg border border-slate-200 outline-none text-[13px] font-medium" />
                    <select value={o.category_slug || ''} onChange={e => updateOpt(qi, oi, { category_slug: e.target.value })}
                      className="md:col-span-3 h-9 px-2 rounded-lg border border-slate-200 outline-none text-[13px] bg-white">
                      <option value="">— Catégorie —</option>
                      {cats.map(c => <option key={c.id} value={c.slug}>{c.name}</option>)}
                    </select>
                    <input value={Array.isArray(o.keywords) ? o.keywords.join(', ') : (o.keywords || '')} onChange={e => updateOpt(qi, oi, { keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                      placeholder="mots-clés (ex: rond, transparent)" className="md:col-span-3 h-9 px-2.5 rounded-lg border border-slate-200 outline-none text-[13px]" />
                    <input type="number" value={o.price_min ?? ''} onChange={e => updateOpt(qi, oi, { price_min: e.target.value })} placeholder="€ min"
                      className="md:col-span-1 h-9 px-2 rounded-lg border border-slate-200 outline-none text-[13px]" />
                    <input type="number" value={o.price_max ?? ''} onChange={e => updateOpt(qi, oi, { price_max: e.target.value })} placeholder="€ max"
                      className="md:col-span-1 h-9 px-2 rounded-lg border border-slate-200 outline-none text-[13px]" />
                    <button onClick={() => removeOption(qi, oi)} className="md:col-span-1 text-slate-300 hover:text-rose-500 flex justify-center"><Trash size={15} weight="bold" /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => addOption(qi)} className="mt-3 text-[13px] font-semibold text-rose-500 hover:text-rose-700 flex items-center gap-1"><Plus size={14} weight="bold" /> Ajouter une réponse</button>
            </div>
          ))}
          <button onClick={addQuestion} className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-500 font-semibold flex items-center justify-center gap-2" data-testid="add-question"><Plus size={18} weight="bold" /> Ajouter une question</button>
        </div>
      )}
    </div>
  );
};

export default KioskQuestionsAdminPage;
